import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';

import { useUser } from '../../context/UserContext';
import { apiFetch } from '../../utils/api';
import { CLIENT_LIST_PAGINATION_HEIGHT_PX, CLIENT_ROW_HEIGHT_PX, MEMBER_ROWS_PER_PAGE_OPTIONS } from './clientsTokens';
import {
  type ClientMember,
  type ManagedClient,
  readClientsApiError,
} from './clientsUtils';

interface ClientsResponse {
  data?: {
    clients?: ManagedClient[];
  };
}

interface MembersResponse {
  data?: {
    members?: ClientMember[];
  };
}

interface ClientCreateResponse {
  data?: {
    client?: ManagedClient;
  };
}

export function useClientsPage() {
  const { isInternalUser, isPoweruser, refreshUser } = useUser();
  const [clients, setClients] = useState<ManagedClient[]>([]);
  const [selectedClientUuid, setSelectedClientUuid] = useState<string | null>(null);
  const [members, setMembers] = useState<ClientMember[]>([]);
  const [clientName, setClientName] = useState('');
  const [memberEmail, setMemberEmail] = useState('');
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<ClientMember | null>(null);
  const [clientSearch, setClientSearch] = useState('');
  const [clientPage, setClientPage] = useState(1);
  const [clientsPageSize, setClientsPageSize] = useState(10);
  const clientListPanelRef = useRef<HTMLDivElement | null>(null);
  const clientListToolbarRef = useRef<HTMLDivElement | null>(null);
  const [memberSearch, setMemberSearch] = useState('');
  const [memberPage, setMemberPage] = useState(0);
  const [memberRowsPerPage, setMemberRowsPerPage] = useState<number>(MEMBER_ROWS_PER_PAGE_OPTIONS[0]);

  const selectedClient = useMemo(
    () => clients.find((client) => client.externalUuid === selectedClientUuid) ?? null,
    [clients, selectedClientUuid],
  );

  const filteredClients = useMemo(() => {
    const query = clientSearch.trim().toLowerCase();
    const matched = query ? clients.filter((client) => client.name.toLowerCase().includes(query)) : clients;
    return [...matched].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  }, [clients, clientSearch]);

  const clientPageCount = Math.max(1, Math.ceil(filteredClients.length / clientsPageSize));
  const pagedClients = useMemo(
    () => filteredClients.slice((clientPage - 1) * clientsPageSize, clientPage * clientsPageSize),
    [filteredClients, clientPage, clientsPageSize],
  );

  const filteredMembers = useMemo(() => {
    const query = memberSearch.trim().toLowerCase();
    if (!query) return members;
    return members.filter(
      (member) => member.email.toLowerCase().includes(query) || member.name.toLowerCase().includes(query),
    );
  }, [members, memberSearch]);

  const pagedMembers = useMemo(
    () => filteredMembers.slice(memberPage * memberRowsPerPage, memberPage * memberRowsPerPage + memberRowsPerPage),
    [filteredMembers, memberPage, memberRowsPerPage],
  );

  useEffect(() => {
    if (clientPage > clientPageCount) setClientPage(clientPageCount);
  }, [clientPage, clientPageCount]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredMembers.length / memberRowsPerPage) - 1);
    if (memberPage > maxPage) setMemberPage(maxPage);
  }, [filteredMembers.length, memberRowsPerPage, memberPage]);

  useEffect(() => {
    const el = clientListPanelRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const panelHeight = entries[0]?.contentRect.height ?? 0;
      const toolbarHeight = clientListToolbarRef.current?.getBoundingClientRect().height ?? 0;
      const listHeight = panelHeight - toolbarHeight - CLIENT_LIST_PAGINATION_HEIGHT_PX;
      if (listHeight > 0) {
        setClientsPageSize(Math.max(1, Math.floor(listHeight / CLIENT_ROW_HEIGHT_PX)));
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [isPoweruser, loadingClients]);

  const loadClients = async (): Promise<void> => {
    setLoadingClients(true);
    setError(null);
    try {
      const response = await apiFetch('/api/clients/');
      if (!response.ok) throw new Error(await readClientsApiError(response));
      const payload = (await response.json()) as ClientsResponse;
      const nextClients = payload.data?.clients ?? [];
      setClients(nextClients);
      setSelectedClientUuid((current) => {
        if (current && nextClients.some((client) => client.externalUuid === current)) return current;
        return nextClients[0]?.externalUuid ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load clients.');
    } finally {
      setLoadingClients(false);
    }
  };

  const loadMembers = async (clientUuid: string): Promise<void> => {
    setLoadingMembers(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/clients/${clientUuid}/members/`);
      if (!response.ok) throw new Error(await readClientsApiError(response));
      const payload = (await response.json()) as MembersResponse;
      setMembers(payload.data?.members ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members.');
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    if (!isInternalUser) return;
    void loadClients();
  }, [isInternalUser]);

  useEffect(() => {
    setMemberSearch('');
    setMemberPage(0);
    if (!selectedClientUuid) {
      setMembers([]);
      return;
    }
    void loadMembers(selectedClientUuid);
  }, [selectedClientUuid]);

  const createClient = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const name = clientName.trim();
    if (!name) return;
    setSaving(true);
    setError(null);
    try {
      const response = await apiFetch('/api/clients/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      if (!response.ok) throw new Error(await readClientsApiError(response));
      const payload = (await response.json()) as ClientCreateResponse;
      const created = payload.data?.client;
      setClientName('');
      setClientSearch('');
      setClientPage(1);
      await loadClients();
      await refreshUser();
      if (created) setSelectedClientUuid(created.externalUuid);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create client.');
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!selectedClientUuid) return;
    const email = memberEmail.trim();
    if (!email) return;
    setSaving(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/clients/${selectedClientUuid}/members/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!response.ok) throw new Error(await readClientsApiError(response));
      setMemberEmail('');
      await loadMembers(selectedClientUuid);
      await loadClients();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add member.');
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (membershipUuid: string): Promise<void> => {
    if (!selectedClientUuid) return;
    setSaving(true);
    setError(null);
    try {
      const response = await apiFetch(`/api/clients/${selectedClientUuid}/members/${membershipUuid}/`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error(await readClientsApiError(response));
      await loadMembers(selectedClientUuid);
      await loadClients();
      await refreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove member.');
    } finally {
      setSaving(false);
    }
  };

  const confirmRemoveMember = async (): Promise<void> => {
    if (!memberToRemove) return;
    await removeMember(memberToRemove.externalUuid);
    setMemberToRemove(null);
  };

  return {
    isPoweruser,
    clients,
    selectedClientUuid,
    setSelectedClientUuid,
    selectedClient,
    members,
    clientName,
    setClientName,
    memberEmail,
    setMemberEmail,
    loadingClients,
    loadingMembers,
    saving,
    error,
    memberToRemove,
    setMemberToRemove,
    clientSearch,
    setClientSearch,
    clientPage,
    setClientPage,
    pagedClients,
    clientPageCount,
    filteredClients,
    clientListPanelRef,
    clientListToolbarRef,
    memberSearch,
    setMemberSearch,
    memberPage,
    setMemberPage,
    memberRowsPerPage,
    setMemberRowsPerPage,
    pagedMembers,
    filteredMembers,
    createClient,
    addMember,
    confirmRemoveMember,
  };
}
